<template>
  <div class="page-container">
    <PageHeader title="日志活跃统计" show-breadcrumb>
      <template #actions>
        <el-button :type="showChart ? '' : 'primary'" icon="el-icon-s-data" size="small" @click="showChart = !showChart; showChart && drawChart()">
          {{ showChart ? '表格视图' : '柱状图' }}
        </el-button>
      </template>
    </PageHeader>

    <SearchPanel>
      <el-date-picker v-model="query.slsYear" type="year" value-format="yyyy" placeholder="选择年" size="small" @change="handleSearch" />
      <el-date-picker v-model="query.slsMonth" type="month" value-format="yyyyMM" placeholder="选择月" size="small" @change="handleSearch" />
      <el-date-picker v-model="query.slsDay" type="date" value-format="yyyyMMdd" placeholder="选择日" size="small" @change="handleSearch" />
      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
      </template>
    </SearchPanel>

    <!-- 图表视图 -->
    <div v-if="showChart" class="chart-container">
      <div ref="chartRef" style="width:100%;height:400px;"></div>
    </div>

    <!-- 表格视图 -->
    <TablePanel v-if="!showChart" title="统计列表">
      <el-table :data="list" v-loading="loading" size="small" stripe border>
        <el-table-column type="index" label="序号" width="90" align="center" />
        <el-table-column prop="slsYear" label="年份"  align="center" />
        <el-table-column prop="slsDate" label="日期"  align="center" />
        <el-table-column prop="slsUserNum" label="活跃用户数"  align="center" />
        <el-table-column prop="slsLogNum" label="日志数量"  align="center" />
        <el-table-column prop="slsStatus" label="状态"  align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.slsStatus === 2 ? 'success' : 'info'" size="mini">{{ row.slsStatus === 2 ? '已处理' : '待处理' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="110" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="handleDetail(row)">时间段</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 40, 80, 160, 320]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <!-- 时间段详情弹窗 -->
    <el-dialog title="时间段分布" :visible.sync="detailVisible" width="700px" v-dialog-drag-resize>
      <div ref="chart2Ref" style="width:100%;height:350px;"></div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getSysLogStatisticsList, getSysLogStatisticsDetails } from '@/api/api'

export default {
  name: 'LogStatistics',
  components: { PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      showChart: false,
      list: [],
      query: { slsYear: '', slsMonth: '', slsDay: '' },
      page: { currentPage: 1, pageSize: 10, total: 0 },
      chart: null,
      chart2: null,
      detailVisible: false
    }
  },
  created () { this.fetchList() },
  mounted () { window.addEventListener('resize', this.handleResize) },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    if (this.chart) { this.chart.dispose(); this.chart = null }
    if (this.chart2) { this.chart2.dispose(); this.chart2 = null }
  },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const res = await getSysLogStatisticsList({ ...this.query, currentPage: this.page.currentPage, pageSize: this.page.pageSize })
        this.list = res.data || []
        this.page.total = res.total || 0
        if (this.showChart) this.drawChart()
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleReset () { this.query = { slsYear: '', slsMonth: '', slsDay: '' }; this.handleSearch() },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    handleResize () {
      if (this.chart) this.chart.resize()
      if (this.chart2) this.chart2.resize()
    },
    // 绘制主图表
    drawChart () {
      this.$nextTick(() => {
        if (!this.$refs.chartRef) return
        if (!this.chart) this.chart = this.$echarts.init(this.$refs.chartRef)
        const dates = this.list.map(i => String(i.slsDate))
        const users = this.list.map(i => i.slsUserNum)
        const logs = this.list.map(i => i.slsLogNum)
        this.chart.setOption({
          tooltip: { trigger: 'axis' },
          legend: { data: ['活跃人数', '日志数量'] },
          xAxis: { type: 'category', data: dates },
          yAxis: { type: 'value' },
          series: [
            { name: '活跃人数', type: 'bar', data: users, itemStyle: { color: '#2c5282' } },
            { name: '日志数量', type: 'line', data: logs, itemStyle: { color: '#d69e2e' }, lineStyle: { color: '#d69e2e' } }
          ]
        })
      })
    },
    // 时间段详情
    async handleDetail (row) {
      try {
        const res = await getSysLogStatisticsDetails({ slsId: row.slsId })
        const details = res.data || []
        this.detailVisible = true
        this.$nextTick(() => {
          if (!this.$refs.chart2Ref) return
          if (this.chart2) this.chart2.dispose()
          this.chart2 = this.$echarts.init(this.$refs.chart2Ref)
          const hours = details.map(i => `${i.slsTime}:00`)
          const users = details.map(i => i.slsUserNum)
          const logs = details.map(i => i.slsLogNum)
          this.chart2.setOption({
            tooltip: { trigger: 'axis' },
            legend: { data: ['活跃人数', '日志数量'] },
            xAxis: { type: 'category', data: hours },
            yAxis: { type: 'value' },
            series: [
              { name: '活跃人数', type: 'bar', data: users, itemStyle: { color: '#2c5282' } },
              { name: '日志数量', type: 'line', data: logs, itemStyle: { color: '#d69e2e' }, lineStyle: { color: '#d69e2e' } }
            ]
          })
        })
      } catch (e) {}
    }
  }
}
</script>

<style lang="scss" scoped>
.chart-container { background: #fff; border-radius: 12px; border: 1px solid #e2e8f0; padding: 20px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04); }
</style>
